import { Component, Injector, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { BaseComponent } from "ng/src/app/shared/base.component";
import { BooleanInputDialogEntry, CompleteButtonOptions, FormInputDialogEntry, FormInputDialogForm, HasPlaceHolder, NumberInputDialogEntry, SelectorInputDialogEntry, StringInputDialogEntry, TemplateInputDialogEntry } from 'ng/src/app/shared/form-input-dialog/form-input-dialog.model';
import { FormInputDialogService } from "ng/src/app/shared/form-input-dialog/form-input-dialog.service";
import { PersistentDialogService } from "ng/src/app/shared/persistent-dialog/persistent-dialog.service";
import { take, takeUntil } from "rxjs/operators";

@Component({
	selector: 'lb-form-input-dialog',
	template: `
        <ng-template #bodyTemplate>
            <ng-container *ngIf="details">
                <div *ngFor="let input of details.controls; let index=index" [ngClass]="!input.visible ? 'hidden' : ''" class="p-grid p-fluid p-mt-2">
	                <div *ngIf="input.label" class="p-d-flex p-ai-center p-col-3">
		                <label [for]="keyedId(index)">{{input.label}}</label>
	                </div>
	                <div class="p-d-flex p-ai-center" [ngClass]="input.label ? 'p-col-9' : 'p-col-12'">
		                <input *ngIf="isType(input, StringInputType) as input" (keydown.enter)="continue()"
		                       [tabIndex]="index + 1"
		                       [autofocus]="index === 0" [id]="keyedId(index)" pInputText
		                       [placeholder]="getPlaceholder(input)"
		                       type="text" [formControl]="input.formControl">
		                
		                <input *ngIf="isType(input, NumberInputType) as input" (keydown.enter)="continue()"
		                       [tabIndex]="index + 1"
		                       [autofocus]="index === 0" [id]="keyedId(index)" pInputText
		                       [placeholder]="getPlaceholder(input)"
		                       type="number" [formControl]="input.formControl">
		                
		                <p-inputSwitch *ngIf="isType(input, BooleanInputType) as input" (keydown.enter)="continue()"
		                               [tabindex]="index + 1"
		                               [inputId]="keyedId(index)" [formControl]="input.formControl">
		                </p-inputSwitch>
		                
		                <ng-container *ngIf="isType(input, SelectorInputType) as input">
			                <p-dropdown [formControl]="input.formControl"
			                            [inputId]="keyedId(index)"
			                            [options]="input.options"
			                            [tabindex]="index + 1"
			                            [autofocus]="index === 0"
			                            [placeholder]="getPlaceholder(input)"
			                            filterValue="display"
			                            optionLabel="display"
			                            optionValue="value"
			                            appendTo="body"
			                            class="w-100"
			                            (keydown.enter)="continue()">
			                </p-dropdown>
		                </ng-container>
		                
		                <ng-container *ngIf="isType(input, TemplateInputType) as input">
			                <ng-container
				                *ngTemplateOutlet="input.templateRef; context: createTemplateContext(input.formControl, input.templateOptions)">
			                </ng-container>
		                </ng-container>
	                </div>
                </div>
            </ng-container>
        </ng-template>

        <ng-template #footerTemplate>
            <ng-container *ngIf="details">
              <button (click)="continue()"
                      [disabled]="getButtonOption('disabled') ?? details.formGroup.invalid"
                      [label]="getButtonOption('label') ?? 'Continue'"
                      [ngClass]="getButtonOption('class')"
                      pButton></button>
            </ng-container>
        </ng-template>
	`
})
export class FormInputDialogComponent extends BaseComponent implements OnInit {
	
	@ViewChild('bodyTemplate') bodyTemplate!: TemplateRef<any>;
	@ViewChild('footerTemplate') footerTemplate!: TemplateRef<any>;
	
	get details() {
		return this._details;
	}
	
	private _details?: FormDetails;
	
	constructor(
		private dialogService: PersistentDialogService,
		private formInputDialogService: FormInputDialogService,
		injector: Injector
	) {
		super(injector);
		console.log('Created');
	}
	
	ngOnInit() {
		this.formInputDialogService.register(this);
	}
	
	load(form: FormInputDialogForm) {
		this._details = new FormDetails(form);
		
		this.dialogService.next({
			template: this.bodyTemplate,
			settings: {
				position: form.position,
				header: form.header,
				escapeToClose: form.escapeCloses,
				footer: this.footerTemplate
			}
		});
		
		this.dialogService.closeSubject.pipe(
			takeUntil(this.onDestroy$),
			take(1)
		)
			.subscribe(() => {
				form.onClose?.();
			})
	}
	
	getPlaceholder(input: HasPlaceHolder) {
		return input.placeholder ?? '';
	}
	
	unload() {
		delete this._details;
		this.dialogService.close();
	}
	
	continue() {
		if (this.details?.formGroup?.invalid ?? true) {
			return;
		}
		
		this.details?.input.onComplete?.(this.details!.input.getControlMap());
		this.unload();
	}
	
	getButtonOption<K extends keyof CompleteButtonOptions>(key: K): CompleteButtonOptions[K] | undefined {
		return this.details?.input?.completeButtonOptions?.[key];
	}
	
	readonly BooleanInputType = BooleanInputDialogEntry;
	readonly NumberInputType = NumberInputDialogEntry;
	readonly StringInputType = StringInputDialogEntry;
	readonly SelectorInputType = SelectorInputDialogEntry;
	readonly TemplateInputType = TemplateInputDialogEntry;
}

class FormDetails {
	get controls() {
		return this._controls;
	}
	
	get formGroup() {
		return this._formGroup;
	}
	
	private _controls: FormInputDialogEntry<any>[] = [];
	private _formGroup!: FormGroup;
	
	constructor(
		public readonly input: FormInputDialogForm
	) {
		this.update();
	}
	
	update() {
		this._controls = Object.values(this.input.map);
		this._formGroup = new FormGroup<any>(this.input.getControlMap());
	}
}